/* ============================================================
   Optic Try-On — realistic eyewear SHAPE explorer
   ------------------------------------------------------------
   Live webcam (or uploaded photo) + MediaPipe FaceLandmarker
   for head pose, + procedurally generated 3D frames rendered
   with Three.js physical materials (acetate / metal / crystal)
   and a studio environment map.

   No product SKUs involved — this is a shape & fit explorer.

   Usage:
     import { mountOpticTryOn } from './optic-tryon.js';
     mountOpticTryOn(document.querySelector('#tryon'));
   ============================================================ */

import * as THREE from 'https://cdn.jsdelivr.net/npm/three@0.169.0/build/three.module.js';
import {
  FaceLandmarker,
  FilesetResolver,
} from 'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.14/vision_bundle.mjs';

const WASM_ROOT = 'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.14/wasm';
const MODEL_URL =
  'https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task';

/* Everything below is modelled in CENTIMETRES against an average adult head.
   Outer-canthus (eye corner to eye corner) distance is the reference: ~9.0 cm.
   At runtime the whole frame is scaled by measuredEyeSpan / 9.0. */
const REF_EYE_SPAN = 9.0;

/* Landmark indices from the MediaPipe 478-point face mesh. */
const LM = {
  eyeOuterR: 33,   // subject's right eye, outer corner
  eyeOuterL: 263,  // subject's left eye, outer corner
  eyeInnerR: 133,
  eyeInnerL: 362,
  bridge: 168,     // top of nose bridge, between the eyes
  noseTip: 4,
  chin: 152,
  foreheadTop: 10,
  cheekR: 234,
  cheekL: 454,
  jawR: 172,
  jawL: 397,
  templeR: 54,
  templeL: 284,
  browR: 105,
  browL: 334,
};

/* ============================================================
   1. 2D outline maths — every frame shape starts as a polygon
   ============================================================ */

function superellipse(w, h, n, steps = 160) {
  const pts = [];
  for (let i = 0; i < steps; i++) {
    const t = (i / steps) * Math.PI * 2;
    const c = Math.cos(t);
    const s = Math.sin(t);
    pts.push(
      new THREE.Vector2(
        (Math.sign(c) || 1) * Math.pow(Math.abs(c), 2 / n) * (w / 2),
        (Math.sign(s) || 1) * Math.pow(Math.abs(s), 2 / n) * (h / 2)
      )
    );
  }
  return pts;
}

/* Smooth closed curve through control points — how the organic shapes
   (wayfarer, aviator, cat-eye) are authored. */
function smoothClosed(controls, w, h, steps = 170) {
  const curve = new THREE.CatmullRomCurve3(
    controls.map((p) => new THREE.Vector3(p[0] * w, p[1] * h, 0)),
    true,
    'catmullrom',
    0.5
  );
  return curve.getPoints(steps).map((v) => new THREE.Vector2(v.x, v.y));
}

/* Straight-edged shapes: dense sampling along each edge keeps the
   Catmull-Rom pass from rounding the corners away. */
function polygonOutline(corners, w, h, perEdge = 14) {
  const pts = [];
  for (let i = 0; i < corners.length; i++) {
    const a = corners[i];
    const b = corners[(i + 1) % corners.length];
    for (let k = 0; k < perEdge; k++) {
      const t = k / perEdge;
      pts.push(
        new THREE.Vector2((a[0] + (b[0] - a[0]) * t) * w, (a[1] + (b[1] - a[1]) * t) * h)
      );
    }
  }
  return pts;
}

function signedArea(pts) {
  let a = 0;
  for (let i = 0; i < pts.length; i++) {
    const p = pts[i];
    const q = pts[(i + 1) % pts.length];
    a += p.x * q.y - q.x * p.y;
  }
  return a / 2;
}

function ensureCCW(pts) {
  return signedArea(pts) < 0 ? pts.slice().reverse() : pts;
}

/* Outward offset of a closed CCW polygon — used to grow the lens
   outline into a rim of a given thickness. */
function offsetPolygon(pts, d) {
  const n = pts.length;
  const out = [];
  for (let i = 0; i < n; i++) {
    const prev = pts[(i - 1 + n) % n];
    const cur = pts[i];
    const next = pts[(i + 1) % n];
    const e1 = new THREE.Vector2(cur.x - prev.x, cur.y - prev.y);
    const e2 = new THREE.Vector2(next.x - cur.x, next.y - cur.y);
    const n1 = new THREE.Vector2(e1.y, -e1.x).normalize();
    const n2 = new THREE.Vector2(e2.y, -e2.x).normalize();
    const nrm = n1.clone().add(n2);
    if (nrm.lengthSq() < 1e-8) nrm.copy(n1);
    nrm.normalize();
    // clamped so a sharp corner (a cat-eye tip) grows a bevel, not a spike
    const cosHalf = Math.max(0.62, nrm.dot(n1));
    out.push(new THREE.Vector2(cur.x + (nrm.x * d) / cosHalf, cur.y + (nrm.y * d) / cosHalf));
  }
  return out;
}

function shapeFromPoints(pts, holePts) {
  const shape = new THREE.Shape(pts);
  if (holePts) shape.holes.push(new THREE.Path(holePts.slice().reverse()));
  return shape;
}

/* ============================================================
   2. Shape catalogue
   ============================================================ */

/* Control points are authored in a unit box (-0.5 .. 0.5) with
   +x = temporal (outer) side and +y = up, then scaled to real cm. */
const SHAPES = [
  {
    id: 'wayfarer',
    name: 'Wayfarer',
    w: 5.2, h: 4.1,
    rim: 0.32, depth: 0.36, style: 'acetate',
    desc: 'The trapezoid classic. Thick acetate rim with a strong brow line — adds width and definition to narrower faces.',
    suits: ['oval', 'round', 'oblong', 'heart'],
    outline: (w, h) =>
      smoothClosed(
        [
          [0.50, 0.28], [0.28, 0.44], [-0.06, 0.48], [-0.44, 0.38],
          [-0.50, 0.06], [-0.38, -0.28], [-0.10, -0.46], [0.24, -0.44],
          [0.46, -0.26], [0.52, 0.02],
        ], w, h
      ),
  },
  {
    id: 'aviator',
    name: 'Aviator',
    w: 5.4, h: 4.6,
    rim: 0.14, depth: 0.2, style: 'metal', doubleBridge: true, nosePads: true,
    desc: 'Teardrop lens, thin metal rim, double bridge. Wide at the brow and tapering low — softens angular jawlines.',
    suits: ['square', 'heart', 'oval'],
    outline: (w, h) =>
      smoothClosed(
        [
          [0.50, 0.26], [0.30, 0.44], [-0.08, 0.48], [-0.44, 0.36],
          [-0.50, 0.06], [-0.44, -0.18], [-0.24, -0.40], [0.02, -0.50],
          [0.28, -0.40], [0.46, -0.16], [0.52, 0.04],
        ], w, h
      ),
  },
  {
    id: 'round',
    name: 'Round',
    w: 4.7, h: 4.7,
    rim: 0.13, depth: 0.19, style: 'metal', nosePads: true,
    desc: 'A true circle in thin metal. Pure curve — the strongest counterpoint to a square or rectangular face.',
    suits: ['square', 'oblong', 'diamond'],
    outline: (w, h) => superellipse(w, h, 2),
  },
  {
    id: 'cateye',
    name: 'Cat-eye',
    w: 5.2, h: 4.0,
    rim: 0.29, depth: 0.33, style: 'acetate',
    desc: 'Upswept temporal corners. Lifts the eye line and draws attention upward — balances a heavier jaw.',
    suits: ['round', 'square', 'diamond', 'oval'],
    // authored tip-first and monotone around the rim: a sharp reversal here
    // makes the Catmull-Rom overshoot into a wing
    outline: (w, h) =>
      smoothClosed(
        [
          [0.56, 0.42], [0.54, 0.22], [0.46, -0.10], [0.24, -0.36],
          [-0.08, -0.44], [-0.36, -0.30], [-0.50, 0.04], [-0.42, 0.28],
          [-0.14, 0.36], [0.20, 0.38], [0.42, 0.40],
        ], w, h
      ),
  },
  {
    id: 'rectangle',
    name: 'Rectangle',
    w: 5.4, h: 3.4,
    rim: 0.24, depth: 0.30, style: 'acetate',
    desc: 'Wide, shallow and straight-edged. Adds horizontal line to a round face and reads as the most conservative shape.',
    suits: ['round', 'oval', 'heart'],
    outline: (w, h) => superellipse(w, h, 6),
  },
  {
    id: 'square',
    name: 'Square',
    w: 5.1, h: 4.4,
    rim: 0.34, depth: 0.38, style: 'acetate',
    desc: 'Oversized and boxy with softly rounded corners. Structural, fashion-forward, and very forgiving on a round face.',
    suits: ['round', 'oval', 'heart'],
    outline: (w, h) => superellipse(w, h, 4.2),
  },
  {
    id: 'oval',
    name: 'Oval',
    w: 5.2, h: 3.7,
    rim: 0.20, depth: 0.26, style: 'acetate',
    desc: 'The neutral shape — gently curved, no strong corners. The safest fit across almost every face type.',
    suits: ['square', 'oblong', 'diamond', 'heart'],
    outline: (w, h) => superellipse(w, h, 2.2),
  },
  {
    id: 'browline',
    name: 'Browline',
    w: 5.2, h: 4.0,
    rim: 0.15, depth: 0.24, style: 'combo',
    desc: 'Heavy acetate brow over a thin metal lower rim. Mimics the eyebrow line — strong, retro, intellectual.',
    suits: ['oval', 'round', 'heart', 'oblong'],
    outline: (w, h) =>
      smoothClosed(
        [
          [0.50, 0.30], [0.26, 0.44], [-0.08, 0.48], [-0.44, 0.36],
          [-0.50, 0.04], [-0.36, -0.30], [-0.06, -0.46], [0.26, -0.42],
          [0.46, -0.22], [0.52, 0.04],
        ], w, h
      ),
  },
  {
    id: 'hexagon',
    name: 'Hexagonal',
    w: 5.0, h: 4.3,
    rim: 0.13, depth: 0.19, style: 'metal', nosePads: true, bridge: 2.0,
    desc: 'Faceted geometric rim in wire metal. Angular without being heavy — a modern alternative to the round.',
    suits: ['round', 'oval', 'square'],
    // the point goes on the temporal side and the nose side gets a short flat
    // edge — a symmetric hexagon aims its inner vertex straight at the bridge
    // bar and the pair reads as one zigzag rather than two lenses
    outline: (w, h) =>
      polygonOutline(
        [
          [0.52, 0.10], [0.18, 0.48], [-0.34, 0.44],
          [-0.50, 0.02], [-0.26, -0.46], [0.26, -0.40],
        ], w, h
      ),
  },
  {
    id: 'pilot',
    name: 'Navigator',
    w: 5.5, h: 4.2,
    rim: 0.27, depth: 0.32, style: 'acetate',
    desc: 'Squared-off aviator in acetate — flat top bar, rounded base. Bold width for a strong-featured face.',
    suits: ['oval', 'round', 'heart'],
    outline: (w, h) =>
      smoothClosed(
        [
          [0.50, 0.34], [0.20, 0.46], [-0.20, 0.46], [-0.50, 0.34],
          [-0.50, 0.00], [-0.34, -0.34], [0.00, -0.48],
          [0.34, -0.34], [0.50, 0.00],
        ], w, h
      ),
  },
  {
    id: 'rimless',
    name: 'Rimless',
    w: 5.0, h: 3.4,
    rim: 0.0, depth: 0.0, style: 'rimless', nosePads: true,
    desc: 'Lens only, drilled to a wire temple. Visually disappears — the least intrusive shape on any face.',
    suits: ['oval', 'round', 'square', 'heart', 'oblong', 'diamond'],
    outline: (w, h) => superellipse(w, h, 2.6),
  },
  {
    id: 'shield',
    // one lens spanning the whole face, so the width is the full frame width
    // (~13.5 cm) rather than a single lens width, with a notch for the nose
    w: 13.4, h: 4.6,
    name: 'Shield',
    rim: 0.18, depth: 0.26, style: 'metal', bend: 0.03,
    desc: 'A single wrapped mask lens across both eyes. Maximum coverage and the most aggressive wrap — a sport/statement silhouette.',
    suits: ['oval', 'oblong', 'diamond'],
    outline: (w, h) =>
      smoothClosed(
        [
          [-0.48, 0.34], [-0.30, 0.46], [0, 0.50], [0.30, 0.46], [0.48, 0.34],
          [0.50, 0.06], [0.44, -0.30], [0.22, -0.46], [0.08, -0.34],
          [0, -0.26], [-0.08, -0.34], [-0.22, -0.46], [-0.44, -0.30], [-0.50, 0.06],
        ], w, h
      ),
  },
];

/* ============================================================
   3. Materials
   ============================================================ */

function tortoiseTexture() {
  const c = document.createElement('canvas');
  c.width = c.height = 256;
  const g = c.getContext('2d');
  g.fillStyle = '#8a5a24';
  g.fillRect(0, 0, 256, 256);
  const blobs = [
    ['#3a2109', 60], ['#20130530', 40], ['#c08a3e', 34], ['#5a3410', 46],
  ];
  for (let i = 0; i < 90; i++) {
    const [col, size] = blobs[i % blobs.length];
    g.fillStyle = col;
    g.beginPath();
    g.ellipse(
      Math.random() * 256, Math.random() * 256,
      size * (0.35 + Math.random() * 0.8), size * (0.25 + Math.random() * 0.7),
      Math.random() * Math.PI, 0, Math.PI * 2
    );
    g.fill();
  }
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  tex.repeat.set(0.18, 0.18);
  return tex;
}

/* Faint irregularity in the surface finish. Perfectly uniform roughness is one
   of the strongest "this is CG" cues — real acetate and plated metal have
   micro-variation that breaks the highlight up. */
let _microTex = null;
function microSurface() {
  if (_microTex) return _microTex;
  const c = document.createElement('canvas');
  c.width = c.height = 128;
  const g = c.getContext('2d');
  const img = g.createImageData(128, 128);
  for (let i = 0; i < img.data.length; i += 4) {
    const v = 118 + Math.random() * 34;
    img.data[i] = img.data[i + 1] = img.data[i + 2] = v;
    img.data[i + 3] = 255;
  }
  g.putImageData(img, 0, 0);
  g.filter = 'blur(1px)';
  g.drawImage(c, 0, 0);
  _microTex = new THREE.CanvasTexture(c);
  _microTex.wrapS = _microTex.wrapT = THREE.RepeatWrapping;
  _microTex.repeat.set(3, 3);
  return _microTex;
}

const FINISHES = [
  {
    id: 'black', name: 'Black acetate', swatch: '#101014',
    make: () => new THREE.MeshPhysicalMaterial({
      color: 0x0c0c10, roughness: 0.28, metalness: 0.0,
      clearcoat: 1, clearcoatRoughness: 0.05, envMapIntensity: 1.1,
    }),
  },
  {
    id: 'tortoise', name: 'Tortoiseshell', swatch: 'linear-gradient(135deg,#a8712f,#3a2109 60%,#c08a3e)',
    make: () => new THREE.MeshPhysicalMaterial({
      map: tortoiseTexture(), roughness: 0.3, metalness: 0.0,
      clearcoat: 1, clearcoatRoughness: 0.06, envMapIntensity: 1.1,
    }),
  },
  {
    id: 'crystal', name: 'Crystal / clear', swatch: 'linear-gradient(135deg,#f2eee7,#c9c2b6)',
    make: () => new THREE.MeshPhysicalMaterial({
      color: 0xe8e2d8, roughness: 0.08, metalness: 0.0,
      transparent: true, opacity: 0.62,
      clearcoat: 1, clearcoatRoughness: 0.03, envMapIntensity: 1.5,
    }),
  },
  {
    id: 'havana', name: 'Havana brown', swatch: '#6b3f1d',
    make: () => new THREE.MeshPhysicalMaterial({
      color: 0x53300f, roughness: 0.3, metalness: 0.0,
      clearcoat: 1, clearcoatRoughness: 0.07, envMapIntensity: 1.1,
    }),
  },
  {
    id: 'gold', name: 'Gold metal', swatch: 'linear-gradient(135deg,#f0d896,#b8912f)',
    make: () => new THREE.MeshPhysicalMaterial({
      color: 0xc9a227, roughness: 0.19, metalness: 1.0, envMapIntensity: 1.5,
    }),
  },
  {
    id: 'silver', name: 'Silver metal', swatch: 'linear-gradient(135deg,#f2f3f6,#a9adb6)',
    make: () => new THREE.MeshPhysicalMaterial({
      color: 0xd7d9de, roughness: 0.15, metalness: 1.0, envMapIntensity: 1.6,
    }),
  },
  {
    id: 'gunmetal', name: 'Gunmetal', swatch: 'linear-gradient(135deg,#6b7079,#33373d)',
    make: () => new THREE.MeshPhysicalMaterial({
      color: 0x4a4e54, roughness: 0.34, metalness: 1.0, envMapIntensity: 1.3,
    }),
  },
  {
    id: 'rosegold', name: 'Rose gold', swatch: 'linear-gradient(135deg,#f0c3ac,#b87554)',
    make: () => new THREE.MeshPhysicalMaterial({
      color: 0xc2836a, roughness: 0.2, metalness: 1.0, envMapIntensity: 1.5,
    }),
  },
];

function gradientAlphaTexture() {
  const c = document.createElement('canvas');
  c.width = 8; c.height = 128;
  const g = c.getContext('2d');
  const grd = g.createLinearGradient(0, 0, 0, 128);
  grd.addColorStop(0, '#ffffff');
  grd.addColorStop(0.55, '#8a8a8a');
  grd.addColorStop(1, '#161616');
  g.fillStyle = grd;
  g.fillRect(0, 0, 8, 128);
  return new THREE.CanvasTexture(c);
}

const LENSES = [
  { id: 'clear', name: 'Clear (optical)', swatch: 'linear-gradient(135deg,#f6f8fb,#dfe6ee)', color: 0xdfe8f2, opacity: 0.16, rough: 0.02 },
  { id: 'smoke', name: 'Smoke', swatch: '#2b2b30', color: 0x14151a, opacity: 0.78, rough: 0.03 },
  { id: 'gradient', name: 'Gradient smoke', swatch: 'linear-gradient(#3a3a42,#0d0d10)', color: 0x17181d, opacity: 0.8, rough: 0.03, gradient: true },
  { id: 'g15', name: 'G-15 green', swatch: '#2a3a2c', color: 0x1d2e20, opacity: 0.76, rough: 0.03 },
  { id: 'brown', name: 'Brown', swatch: '#4a2d14', color: 0x3d2410, opacity: 0.74, rough: 0.03 },
  { id: 'blue', name: 'Blue mirror', swatch: 'linear-gradient(135deg,#7fb0ff,#1b3f8a)', color: 0x2a4f9e, opacity: 0.82, rough: 0.02, mirror: true },
  { id: 'rose', name: 'Rose', swatch: 'linear-gradient(135deg,#e8a3b4,#7a3245)', color: 0x8a3a4c, opacity: 0.7, rough: 0.03 },
  { id: 'photo', name: 'Photochromic', swatch: 'linear-gradient(135deg,#c9ccd4,#4a4d55)', color: 0x6a6d76, opacity: 0.5, rough: 0.02 },
];

function makeLensMaterial(def, envMap) {
  const mat = new THREE.MeshPhysicalMaterial({
    color: new THREE.Color(def.color),
    metalness: def.mirror ? 0.9 : 0.0,
    roughness: def.rough,
    transparent: true,
    opacity: def.opacity,
    clearcoat: 1,
    clearcoatRoughness: 0.02,
    envMap,
    envMapIntensity: def.mirror ? 2.6 : 1.5,
    side: THREE.DoubleSide,
    depthWrite: false,
    premultipliedAlpha: false,
  });
  if (def.gradient) {
    mat.alphaMap = gradientAlphaTexture();
  }
  return mat;
}

/* ============================================================
   4. Frame construction
   ============================================================ */

/* Spherical base curve: z -= (x² + y²) · amount.
   Normals are transformed analytically by the bend's inverse-transpose Jacobian
   rather than recomputed. computeVertexNormals would average across the coarse
   triangulation a wide lens gets from earcut and shade it in visible facets —
   and would also round off the rim's deliberately crisp extruded edges. */
function curveLens(geo, amount = 0.012) {
  const pos = geo.attributes.position;
  const nrm = geo.attributes.normal;
  for (let i = 0; i < pos.count; i++) {
    const x = pos.getX(i);
    const y = pos.getY(i);
    if (nrm) {
      const nx = nrm.getX(i) + 2 * amount * x * nrm.getZ(i);
      const ny = nrm.getY(i) + 2 * amount * y * nrm.getZ(i);
      const nz = nrm.getZ(i);
      const len = Math.hypot(nx, ny, nz) || 1;
      nrm.setXYZ(i, nx / len, ny / len, nz / len);
    }
    pos.setZ(i, pos.getZ(i) - (x * x + y * y) * amount);
  }
  pos.needsUpdate = true;
  if (nrm) nrm.needsUpdate = true;
  return geo;
}

function extrude(shape, depth, bevel) {
  const geo = new THREE.ExtrudeGeometry(shape, {
    depth,
    bevelEnabled: bevel > 0,
    bevelThickness: bevel,
    bevelSize: bevel,
    bevelSegments: 3,
    curveSegments: 12,
    steps: 1,
  });
  geo.translate(0, 0, -depth / 2);
  geo.computeVertexNormals();
  return geo;
}

function tubeAlong(points, radius, tension = 0.5, segments = 60) {
  const curve = new THREE.CatmullRomCurve3(
    points.map((p) => new THREE.Vector3(p[0], p[1], p[2])),
    false, 'catmullrom', tension
  );
  return new THREE.TubeGeometry(curve, segments, radius, 10, false);
}

function extrudeAlong(points, profileW, profileH, tension = 0.5, steps = 70) {
  const curve = new THREE.CatmullRomCurve3(
    points.map((p) => new THREE.Vector3(p[0], p[1], p[2])),
    false, 'catmullrom', tension
  );
  const prof = new THREE.Shape();
  const w = profileW / 2;
  const h = profileH / 2;
  const r = Math.min(w, h) * 0.55;
  prof.moveTo(-w + r, -h);
  prof.lineTo(w - r, -h);
  prof.quadraticCurveTo(w, -h, w, -h + r);
  prof.lineTo(w, h - r);
  prof.quadraticCurveTo(w, h, w - r, h);
  prof.lineTo(-w + r, h);
  prof.quadraticCurveTo(-w, h, -w, h - r);
  prof.lineTo(-w, -h + r);
  prof.quadraticCurveTo(-w, -h, -w + r, -h);
  const geo = new THREE.ExtrudeGeometry(prof, {
    extrudePath: curve, steps, bevelEnabled: false, curveSegments: 8,
  });
  geo.computeVertexNormals();
  return geo;
}

/* Build one complete pair of frames for a given shape + finish + lens.
   `thickness` scales rim, front depth, bridge, hinges and temples together —
   scaling the rim alone leaves wire temples on a chunky front and the frame
   stops reading as one manufactured object. */
function buildFrame(shapeDef, frameMat, lensMat, envMap, thickness = 1) {
  const t = THREE.MathUtils.clamp(thickness, 0.4, 1.8);
  const group = new THREE.Group();
  const front = new THREE.Group();
  group.add(front);

  const isShield = shapeDef.id === 'shield';
  // base curve, applied to lens AND rim together so a wrapped front stays one piece
  const bend = shapeDef.bend || 0.012;
  const zAt = (x) => -(x * x) * bend;
  const outlineRaw = ensureCCW(shapeDef.outline(shapeDef.w, shapeDef.h));
  const bridgeGap = isShield ? 0.0 : shapeDef.bridge || 1.75;
  const lensCX = isShield ? 0 : (shapeDef.w + bridgeGap) / 2;
  const rimT = shapeDef.rim * t;          // rim thickness, scaled
  const depthT = shapeDef.depth * t;      // front depth, scaled
  const halfWidth = lensCX + shapeDef.w / 2 + rimT;

  const metalMat = frameMat.metalness > 0.5
    ? frameMat
    : new THREE.MeshPhysicalMaterial({
        color: 0x9aa0aa, metalness: 1, roughness: 0.25, envMap, envMapIntensity: 1.4,
      });

  const sides = isShield ? [0] : [-1, 1];

  for (const side of sides) {
    const eye = new THREE.Group();
    // +x is the temporal side in the authored outline; mirror for the other eye
    const outline = outlineRaw.map((p) => new THREE.Vector2(p.x * (side === 0 ? 1 : side), p.y));
    const pts = ensureCCW(outline);

    // --- lens ---
    const lensGeo = curveLens(new THREE.ShapeGeometry(shapeFromPoints(pts), 12), bend);
    const lens = new THREE.Mesh(lensGeo, lensMat);
    lens.name = 'lens';
    lens.renderOrder = 2;
    eye.add(lens);

    // --- rim ---
    if (shapeDef.style !== 'rimless' && rimT > 0) {
      const outer = offsetPolygon(pts, rimT);
      const rimGeo = curveLens(
        extrude(shapeFromPoints(outer, pts), depthT, Math.min(0.06, depthT * 0.22)),
        bend
      );
      const rim = new THREE.Mesh(rimGeo, shapeDef.style === 'combo' ? metalMat : frameMat);
      rim.renderOrder = 3;
      eye.add(rim);

      // browline: heavy acetate bar spanning from the lens edge outward,
      // sitting above the thin metal rim rather than on top of the lens
      if (shapeDef.style === 'combo') {
        const cut = shapeDef.h * 0.12;
        const inner = pts.filter((p) => p.y > cut).sort((a, b) => a.x - b.x);
        const outerBar = offsetPolygon(pts, 0.55 * t).filter((p) => p.y > cut).sort((a, b) => a.x - b.x);
        if (inner.length > 2 && outerBar.length > 2) {
          const band = ensureCCW(inner.concat(outerBar.reverse()));
          const bar = extrude(shapeFromPoints(band), depthT * 2.4, 0.07);
          const barMesh = new THREE.Mesh(bar, frameMat);
          barMesh.position.z = 0.05;
          barMesh.renderOrder = 3;
          eye.add(barMesh);
        }
      }
    }

    // --- rimless drill points ---
    if (shapeDef.style === 'rimless') {
      const inner = pts.reduce((a, p) => ((side < 0 ? p.x > a.x : p.x < a.x) ? p : a), pts[0]);
      const outerP = pts.reduce((a, p) => ((side < 0 ? p.x < a.x : p.x > a.x) ? p : a), pts[0]);
      for (const p of [inner, outerP]) {
        const stud = new THREE.Mesh(new THREE.CylinderGeometry(0.07, 0.07, 0.3, 12), metalMat);
        stud.rotation.x = Math.PI / 2;
        stud.position.set(p.x, p.y, 0);
        eye.add(stud);
      }
    }

    // face-form wrap: each lens toes outward a few degrees
    if (side !== 0) {
      eye.position.x = lensCX * side;
      eye.rotation.y = THREE.MathUtils.degToRad(-(shapeDef.wrap || 6) * side);
    }
    front.add(eye);
  }

  // --- bridge ---
  if (!isShield) {
    const bTop = shapeDef.h * 0.22;
    const bridgeMat = shapeDef.style === 'metal' || shapeDef.style === 'rimless' ? metalMat : frameMat;
    const inner = lensCX - shapeDef.w / 2;
    const arcs = shapeDef.doubleBridge ? [bTop, shapeDef.h * 0.46] : [bTop];
    for (const y of arcs) {
      const pts = [
        [-inner - 0.1, y - 0.15, 0.05],
        [-inner * 0.45, y + 0.22, 0.12],
        [0, y + 0.3, 0.14],
        [inner * 0.45, y + 0.22, 0.12],
        [inner + 0.1, y - 0.15, 0.05],
      ];
      const geo =
        shapeDef.style === 'acetate'
          ? extrudeAlong(pts, depthT * 0.9, 0.55 * t, 0.5, 40)
          : tubeAlong(pts, 0.075 * t, 0.5, 40);
      front.add(new THREE.Mesh(geo, bridgeMat));
    }
    if (shapeDef.doubleBridge) {
      for (const s of [-1, 1]) {
        const bar = new THREE.Mesh(new THREE.CylinderGeometry(0.055, 0.055, shapeDef.h * 0.26, 10), metalMat);
        bar.position.set(s * inner * 0.55, shapeDef.h * 0.34, 0.12);
        front.add(bar);
      }
    }
  }

  // --- nose pads ---
  if (shapeDef.nosePads) {
    for (const s of [-1, 1]) {
      const pad = new THREE.Mesh(
        new THREE.SphereGeometry(0.3, 16, 12),
        new THREE.MeshPhysicalMaterial({
          color: 0xf2f4f7, roughness: 0.12, metalness: 0,
          transparent: true, opacity: 0.6, clearcoat: 1, envMap, envMapIntensity: 1.2,
        })
      );
      pad.scale.set(0.42, 1.0, 0.5);
      pad.position.set(s * 0.75, -shapeDef.h * 0.14, -0.55);
      pad.rotation.z = s * 0.25;
      front.add(pad);
      const arm = new THREE.Mesh(tubeAlong(
        [[s * (lensCX - shapeDef.w / 2), shapeDef.h * 0.12, 0], [s * 0.95, -shapeDef.h * 0.02, -0.35], [s * 0.78, -shapeDef.h * 0.12, -0.5]],
        0.045, 0.5, 20
      ), metalMat);
      front.add(arm);
    }
  }

  // --- hinges + temples ---
  const templeY = shapeDef.h * (isShield ? 0.24 : 0.3);
  const isThick = shapeDef.style === 'acetate' || shapeDef.style === 'combo';
  for (const s of [-1, 1]) {
    const x0 = halfWidth * (isShield ? 0.98 : 1);
    // temples splay out to head width whatever the lens width is, so a narrow
    // frame's arms still clear the cheek (and the occluder) on the way to the ear
    const flare = Math.max(x0 + 0.5, 6.95);
    const path = [
      [s * x0, templeY, zAt(x0)], // meets the rim where the bend actually put it
      [s * (x0 + 0.3), templeY + 0.02, zAt(x0) - 0.9],
      [s * flare, templeY - 0.02, -2.8],
      [s * (flare + 0.05), templeY - 0.12, -5.4],
      [s * flare, templeY - 0.34, -7.9],
      [s * (flare - 0.1), templeY - 1.5, -9.4],
      [s * (flare - 0.15), templeY - 2.4, -9.7],
    ];
    const geo = isThick
      ? extrudeAlong(path, 0.28 * t, 0.85 * t, 0.5, 90)
      : tubeAlong(path, 0.075 * t, 0.5, 90);
    const temple = new THREE.Mesh(geo, isThick ? frameMat : metalMat);
    temple.name = 'temple';
    group.add(temple);

    const hinge = new THREE.Mesh(new THREE.BoxGeometry(0.42 * t, 0.5 * t, 0.34 * t), metalMat);
    hinge.position.set(s * (x0 + 0.1), templeY, zAt(x0) - 0.3);
    group.add(hinge);
  }

  // pantoscopic tilt — real frames lean the lens plane back at the top
  group.rotation.x = THREE.MathUtils.degToRad(-7);

  return group;
}

/* ============================================================
   5. Studio environment (built in-code, no external HDR file)
   ============================================================ */

function buildStudioEnv(renderer) {
  const scene = new THREE.Scene();

  const c = document.createElement('canvas');
  c.width = 4; c.height = 256;
  const g = c.getContext('2d');
  const grd = g.createLinearGradient(0, 0, 0, 256);
  grd.addColorStop(0.0, '#eef2f7');
  grd.addColorStop(0.45, '#8d949e');
  grd.addColorStop(0.55, '#4a4e55');
  grd.addColorStop(1.0, '#14161a');
  g.fillStyle = grd;
  g.fillRect(0, 0, 4, 256);
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;

  const dome = new THREE.Mesh(
    new THREE.SphereGeometry(50, 24, 16),
    new THREE.MeshBasicMaterial({ map: tex, side: THREE.BackSide })
  );
  scene.add(dome);

  // soft box key + fill + rim, the way a product photographer would set it
  const panel = (w, h, color, pos, look) => {
    const m = new THREE.Mesh(
      new THREE.PlaneGeometry(w, h),
      new THREE.MeshBasicMaterial({ color, side: THREE.DoubleSide })
    );
    m.position.set(...pos);
    m.lookAt(...(look || [0, 0, 0]));
    scene.add(m);
  };
  panel(26, 18, 0xffffff, [-14, 12, 16]);
  panel(20, 14, 0xcfd8e4, [18, 4, 12]);
  panel(30, 8, 0xffffff, [0, 22, -6]);
  panel(16, 10, 0x6f7a8a, [0, -14, 14]);

  const pmrem = new THREE.PMREMGenerator(renderer);
  pmrem.compileEquirectangularShader();
  const rt = pmrem.fromScene(scene, 0.04);
  pmrem.dispose();
  dome.geometry.dispose();
  return rt.texture;
}

/* ============================================================
   6. Face-shape analysis
   ============================================================ */

const FACE_SHAPES = {
  oval: 'Oval — balanced proportions, slightly longer than wide. Almost every frame shape works; you can pick on style alone.',
  round: 'Round — soft curves, width close to length. Angular frames add the definition your face doesn\'t have on its own.',
  square: 'Square — strong jaw, wide forehead, straight sides. Curved and rounded frames soften those angles.',
  oblong: 'Oblong — noticeably longer than wide. Deeper frames with a strong brow break up the length.',
  heart: 'Heart — wider forehead tapering to a narrow chin. Frames that are wider at the bottom rebalance the taper.',
  diamond: 'Diamond — narrow forehead and jaw, wide cheekbones. Frames with detail at the brow line draw the eye upward.',
};

function analyseFace(pt) {
  const d = (a, b) => Math.hypot(pt[a].x - pt[b].x, pt[a].y - pt[b].y);
  const cheek = d(LM.cheekR, LM.cheekL);
  const jaw = d(LM.jawR, LM.jawL);
  const forehead = d(LM.templeR, LM.templeL);
  const length = d(LM.foreheadTop, LM.chin);

  const lw = length / cheek;              // how long the face is
  const jc = jaw / cheek;                 // jaw vs cheekbone
  const fc = forehead / cheek;            // forehead vs cheekbone

  let shape;
  if (lw > 1.5) shape = 'oblong';
  else if (jc > 0.92 && lw < 1.32) shape = 'square';
  else if (lw < 1.28 && jc < 0.9) shape = 'round';
  else if (fc > 0.98 && jc < 0.82) shape = 'heart';
  else if (fc < 0.88 && jc < 0.84) shape = 'diamond';
  else shape = 'oval';

  return { shape, ratios: { lw, jc, fc } };
}

/* Exposed for tooling (see preview.html) and for shops that want to build
   their own UI around the same geometry. */
export { SHAPES, FINISHES, LENSES, buildFrame, buildStudioEnv, makeLensMaterial, analyseFace };

/* ============================================================
   7. Widget
   ============================================================ */

const ICONS = {
  camera: '<svg viewBox="0 0 24 24"><path d="M3 8.5A2.5 2.5 0 0 1 5.5 6h1.7a1 1 0 0 0 .83-.45l.94-1.4A1 1 0 0 1 9.8 3.7h4.4a1 1 0 0 1 .83.45l.94 1.4a1 1 0 0 0 .83.45h1.7A2.5 2.5 0 0 1 21 8.5v9A2.5 2.5 0 0 1 18.5 20h-13A2.5 2.5 0 0 1 3 17.5z"/><circle cx="12" cy="13" r="3.6"/></svg>',
  mirror: '<svg viewBox="0 0 24 24"><path d="M12 3v18"/><path d="M8 7 3 12l5 5"/><path d="m16 7 5 5-5 5"/></svg>',
  upload: '<svg viewBox="0 0 24 24"><path d="M12 16V4"/><path d="m7 9 5-5 5 5"/><path d="M4 16v3a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-3"/></svg>',
  shot: '<svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="3.5"/></svg>',
};

/* SVG thumbnail for the shape picker, generated from the same outline
   maths the 3D frame uses — so the icon is always truthful. */
function shapeThumb(def) {
  const pts = def.outline(def.w, def.h);
  let minX = 1e9, maxX = -1e9, minY = 1e9, maxY = -1e9;
  pts.forEach((p) => {
    minX = Math.min(minX, p.x); maxX = Math.max(maxX, p.x);
    minY = Math.min(minY, p.y); maxY = Math.max(maxY, p.y);
  });
  const gap = def.id === 'shield' ? 0 : def.bridge || 1.75;
  const cx = def.id === 'shield' ? 0 : (def.w + gap) / 2;
  const path = (sx) =>
    pts.map((p, i) => `${i ? 'L' : 'M'}${(p.x * sx + cx * sx).toFixed(2)} ${(-p.y).toFixed(2)}`).join('') + 'Z';
  const body = def.id === 'shield' ? path(1) : path(1) + path(-1);
  const bridgePath =
    def.id === 'shield' ? '' : `<path d="M${(cx - def.w / 2).toFixed(2)} ${(-def.h * 0.22).toFixed(2)}Q0 ${(-def.h * 0.34).toFixed(2)} ${(-(cx - def.w / 2)).toFixed(2)} ${(-def.h * 0.22).toFixed(2)}"/>`;
  const totalW = (cx + maxX) * 2 + 1.6;
  const totalH = (maxY - minY) + 1.6;
  return `<svg viewBox="${(-totalW / 2).toFixed(2)} ${(-totalH / 2).toFixed(2)} ${totalW.toFixed(2)} ${totalH.toFixed(2)}"><path d="${body}"/>${bridgePath}</svg>`;
}

export function mountOpticTryOn(root, options = {}) {
  const opts = Object.assign(
    { shape: 'wayfarer', finish: 'black', lens: 'clear', theme: 'dark', mirror: true },
    options
  );

  root.classList.add('otr');
  root.dataset.theme = opts.theme;
  root.innerHTML = `
    <div class="otr-stage-wrap">
      <div class="otr-stage${opts.mirror ? ' is-mirrored' : ''}">
        <video class="otr-video" playsinline muted autoplay></video>
        <img class="otr-photo" alt="">
        <canvas class="otr-canvas"></canvas>
        <div class="otr-grain"></div>
        <div class="otr-badge">Shape <b data-otr="badge">—</b></div>
        <div class="otr-stage-tools">
          <button class="otr-icon-btn" data-otr="mirror" title="Mirror view">${ICONS.mirror}</button>
          <button class="otr-icon-btn" data-otr="snap" title="Snapshot">${ICONS.shot}</button>
        </div>
        <div class="otr-hint" data-otr="hint" hidden></div>
        <div class="otr-overlay" data-otr="overlay">
          <div>
            <div class="otr-spinner" data-otr="spinner"></div>
            <h3 data-otr="ov-title">Try frame shapes on your own face</h3>
            <p data-otr="ov-text">Turn on your camera to see how each silhouette actually sits on you. Nothing is uploaded — all tracking runs in your browser.</p>
            <button class="otr-btn otr-btn--primary" data-otr="start">${ICONS.camera} Enable camera</button>
            <button class="otr-btn" data-otr="pick-photo">${ICONS.upload} Use a photo instead</button>
            <input type="file" accept="image/*" hidden data-otr="file">
          </div>
        </div>
      </div>
    </div>

    <div class="otr-panel">
      <div class="otr-group">
        <h4>Frame shape</h4>
        <div class="otr-shapes" data-otr="shapes"></div>
        <div class="otr-desc" data-otr="desc"></div>
      </div>
      <div class="otr-group">
        <h4>Frame finish</h4>
        <div class="otr-swatches" data-otr="finishes"></div>
      </div>
      <div class="otr-group">
        <h4>Lens tint</h4>
        <div class="otr-swatches" data-otr="lenses"></div>
      </div>
      <div class="otr-group">
        <h4>Fit adjustment</h4>
        <div class="otr-slider"><label>Rim thickness <b data-otr="v-thick">100%</b></label>
          <input type="range" data-otr="thick" min="50" max="160" value="100"></div>
        <div class="otr-slider"><label>Frame width <b data-otr="v-size">100%</b></label>
          <input type="range" data-otr="size" min="80" max="125" value="100"></div>
        <div class="otr-slider"><label>Vertical position <b data-otr="v-y">0.0</b></label>
          <input type="range" data-otr="y" min="-15" max="15" value="0"></div>
        <div class="otr-slider"><label>Depth on nose <b data-otr="v-z">0.0</b></label>
          <input type="range" data-otr="z" min="-10" max="15" value="0"></div>
      </div>
      <div class="otr-group">
        <h4>Face shape</h4>
        <button class="otr-btn" data-otr="analyse">Analyse my face shape</button>
        <div class="otr-face-result" data-otr="face" hidden></div>
      </div>
      <div class="otr-group">
        <h4>Snapshots</h4>
        <div class="otr-shots" data-otr="shots"></div>
      </div>
    </div>`;

  const $ = (n) => root.querySelector(`[data-otr="${n}"]`);
  const stage = root.querySelector('.otr-stage');
  const video = root.querySelector('.otr-video');
  const photo = root.querySelector('.otr-photo');
  const canvas = root.querySelector('.otr-canvas');

  /* ---------- three.js ---------- */
  const renderer = new THREE.WebGLRenderer({ canvas, alpha: true, antialias: true, preserveDrawingBuffer: true });
  renderer.setPixelRatio(Math.min(devicePixelRatio, 2));
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.05;

  const scene = new THREE.Scene();
  const CAM_Z = 60; // cm — face sits on the z = 0 plane
  const camera = new THREE.PerspectiveCamera(38, 4 / 3, 1, 400);
  camera.position.set(0, 0, CAM_Z);

  const envMap = buildStudioEnv(renderer);
  scene.environment = envMap;

  const key = new THREE.DirectionalLight(0xffffff, 1.5);
  key.position.set(-6, 9, 14);
  scene.add(key);
  const fill = new THREE.DirectionalLight(0xdfe8f5, 0.6);
  fill.position.set(9, -2, 10);
  scene.add(fill);

  const HEAD_SCALE = [6.6, 9.5, 8.0];
  const HEAD_POS = [0, -1.5, -9.0]; // front pole sits just behind the lens plane

  // depth-only head proxy so temples disappear behind the head on a turn
  const occluder = new THREE.Mesh(
    new THREE.SphereGeometry(1, 32, 24),
    new THREE.MeshBasicMaterial({ colorWrite: false })
  );
  occluder.scale.set(...HEAD_SCALE);
  occluder.position.set(...HEAD_POS);
  occluder.renderOrder = -1;

  const anchor = new THREE.Group();   // follows the head
  const fitGroup = new THREE.Group(); // user fit offsets
  anchor.add(occluder);
  anchor.add(fitGroup);
  scene.add(anchor);

  let frameGroup = null;

  const state = {
    shape: SHAPES.find((s) => s.id === opts.shape) || SHAPES[0],
    finish: FINISHES.find((f) => f.id === opts.finish) || FINISHES[0],
    lens: LENSES.find((l) => l.id === opts.lens) || LENSES[0],
    size: 1, offsetY: 0, offsetZ: 0, thickness: 1,
    tracking: false, mode: null, landmarks: null,
  };

  function rebuild() {
    if (frameGroup) {
      fitGroup.remove(frameGroup);
      frameGroup.traverse((o) => { if (o.isMesh) o.geometry.dispose(); });
    }
    const fm = state.finish.make();
    fm.envMap = envMap;
    fm.roughnessMap = microSurface();
    const lm = makeLensMaterial(state.lens, envMap);
    frameGroup = buildFrame(state.shape, fm, lm, envMap, state.thickness);
    fitGroup.add(frameGroup);
    $('badge').textContent = state.shape.name;
    $('desc').innerHTML = `<b>${state.shape.name}</b>${state.shape.desc}`;
  }

  /* ---------- controls ---------- */
  $('shapes').innerHTML = SHAPES.map(
    (s) => `<button class="otr-shape${s.id === state.shape.id ? ' is-active' : ''}" data-id="${s.id}">
      ${shapeThumb(s)}<span>${s.name}</span></button>`
  ).join('');
  $('shapes').addEventListener('click', (e) => {
    const btn = e.target.closest('.otr-shape');
    if (!btn) return;
    state.shape = SHAPES.find((s) => s.id === btn.dataset.id);
    $('shapes').querySelectorAll('.otr-shape').forEach((b) => b.classList.toggle('is-active', b === btn));
    rebuild();
  });

  $('finishes').innerHTML = FINISHES.map(
    (f) => `<button class="otr-swatch${f.id === state.finish.id ? ' is-active' : ''}" data-id="${f.id}"
      data-label="${f.name}" style="background:${f.swatch}"></button>`
  ).join('');
  $('finishes').addEventListener('click', (e) => {
    const btn = e.target.closest('.otr-swatch');
    if (!btn) return;
    state.finish = FINISHES.find((f) => f.id === btn.dataset.id);
    $('finishes').querySelectorAll('.otr-swatch').forEach((b) => b.classList.toggle('is-active', b === btn));
    rebuild();
  });

  $('lenses').innerHTML = LENSES.map(
    (l) => `<button class="otr-swatch${l.id === state.lens.id ? ' is-active' : ''}" data-id="${l.id}"
      data-label="${l.name}" style="background:${l.swatch}"></button>`
  ).join('');
  $('lenses').addEventListener('click', (e) => {
    const btn = e.target.closest('.otr-swatch');
    if (!btn) return;
    state.lens = LENSES.find((l) => l.id === btn.dataset.id);
    $('lenses').querySelectorAll('.otr-swatch').forEach((b) => b.classList.toggle('is-active', b === btn));
    rebuild();
  });

  /* Thickness changes rebuild geometry, so coalesce to one rebuild per frame —
     a range input fires far faster than the mesh can be regenerated. */
  let thickPending = false;
  $('thick').addEventListener('input', (e) => {
    state.thickness = +e.target.value / 100;
    $('v-thick').textContent = `${e.target.value}%`;
    if (thickPending) return;
    thickPending = true;
    requestAnimationFrame(() => { thickPending = false; rebuild(); });
  });

  $('size').addEventListener('input', (e) => {
    state.size = +e.target.value / 100;
    $('v-size').textContent = `${e.target.value}%`;
  });
  $('y').addEventListener('input', (e) => {
    state.offsetY = +e.target.value / 10;
    $('v-y').textContent = state.offsetY.toFixed(1);
  });
  $('z').addEventListener('input', (e) => {
    state.offsetZ = +e.target.value / 10;
    $('v-z').textContent = state.offsetZ.toFixed(1);
  });

  $('mirror').addEventListener('click', () => stage.classList.toggle('is-mirrored'));

  /* ---------- sizing ---------- */
  /* Checked every frame rather than only on ResizeObserver: the element can
     be laid out late (hidden tab, collapsed section) and we'd miss the event. */
  function resize(srcW, srcH) {
    if (srcW && srcH) stage.style.aspectRatio = `${srcW} / ${srcH}`;
    const w = stage.clientWidth;
    const h = stage.clientHeight;
    if (!w || !h) return false;
    const dpr = Math.min(devicePixelRatio, 2);
    if (canvas.width === Math.floor(w * dpr) && canvas.height === Math.floor(h * dpr)) return false;
    renderer.setSize(w, h, false);
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
    return true;
  }

  /* world extent of the z = 0 plane, used to map normalised landmarks */
  function planeHalfSize() {
    const h = Math.tan(THREE.MathUtils.degToRad(camera.fov / 2)) * CAM_Z;
    return { hw: h * camera.aspect, hh: h };
  }

  function toWorld(lm) {
    const { hw, hh } = planeHalfSize();
    return new THREE.Vector3((lm.x - 0.5) * 2 * hw, -(lm.y - 0.5) * 2 * hh, -lm.z * 2 * hw);
  }

  /* ---------- lighting estimated from the camera image ----------
     A fixed studio rig on top of an arbitrary room is what makes composited 3D
     read as fake: wrong brightness, wrong colour temperature, highlights on the
     wrong side. So sample the face region of the actual frame and drive
     exposure, white balance and key direction from it. */
  const probe = document.createElement('canvas');
  probe.width = probe.height = 24;
  const probeCtx = probe.getContext('2d', { willReadFrequently: true });
  const light = { exposure: 1.05, dirX: -0.35, dirY: 0.5, tint: new THREE.Color(1, 1, 1) };
  let probeTick = 0;

  function estimateLighting(src, pts) {
    const sw = src.videoWidth || src.naturalWidth;
    const sh = src.videoHeight || src.naturalHeight;
    if (!sw) return;

    // crop to the face, padded, so the background doesn't skew the reading
    let x0 = 1, y0 = 1, x1 = 0, y1 = 0;
    for (const i of [LM.cheekR, LM.cheekL, LM.foreheadTop, LM.chin]) {
      x0 = Math.min(x0, pts[i].x); x1 = Math.max(x1, pts[i].x);
      y0 = Math.min(y0, pts[i].y); y1 = Math.max(y1, pts[i].y);
    }
    const cx = THREE.MathUtils.clamp(x0, 0, 1) * sw;
    const cy = THREE.MathUtils.clamp(y0, 0, 1) * sh;
    const cw = Math.max(8, (x1 - x0) * sw);
    const ch = Math.max(8, (y1 - y0) * sh);
    try {
      probeCtx.drawImage(src, cx, cy, cw, ch, 0, 0, 24, 24);
    } catch (e) {
      return; // frame not decodable yet
    }
    const d = probeCtx.getImageData(0, 0, 24, 24).data;

    let r = 0, g = 0, b = 0, left = 0, right = 0, top = 0, bottom = 0;
    for (let py = 0; py < 24; py++) {
      for (let px = 0; px < 24; px++) {
        const i = (py * 24 + px) * 4;
        r += d[i]; g += d[i + 1]; b += d[i + 2];
        const lum = 0.2126 * d[i] + 0.7152 * d[i + 1] + 0.0722 * d[i + 2];
        if (px < 12) left += lum; else right += lum;
        if (py < 12) top += lum; else bottom += lum;
      }
    }
    const n = 24 * 24;
    r /= n * 255; g /= n * 255; b /= n * 255;
    const lum = 0.2126 * r + 0.7152 * g + 0.0722 * b;

    // brighter side of the face tells us roughly where the light is
    const dx = (right - left) / Math.max(1, right + left);
    const dy = (top - bottom) / Math.max(1, top + bottom);

    const k = 0.08; // slow, so the frame doesn't strobe as the webcam auto-exposes
    light.exposure += (THREE.MathUtils.clamp(0.5 + lum * 1.15, 0.45, 1.5) - light.exposure) * k;
    light.dirX += (THREE.MathUtils.clamp(dx * 3.2, -1, 1) - light.dirX) * k;
    light.dirY += (THREE.MathUtils.clamp(0.25 + dy * 2.4, -0.6, 1) - light.dirY) * k;
    if (lum > 0.02) {
      light.tint.lerp(new THREE.Color(r / lum, g / lum, b / lum).lerp(new THREE.Color(1, 1, 1), 0.45), k);
    }

    renderer.toneMappingExposure = light.exposure;
    scene.environmentIntensity = 0.45 + light.exposure * 0.75;
    // kept deliberately frontal: a raking key throws a shadow halfway down the
    // cheek, which reads as the frame floating rather than resting on the face
    key.position.set(light.dirX * 11, light.dirY * 10, 21);
    key.color.copy(light.tint);
    key.intensity = 0.9 + light.exposure * 0.8;
    fill.position.set(-light.dirX * 14, -2, 11);
    fill.color.copy(light.tint).lerp(new THREE.Color(0xdfe8f5), 0.5);
  }

  /* ---------- tracking ---------- */
  let landmarker = null;
  const smooth = {
    pos: new THREE.Vector3(), quat: new THREE.Quaternion(), scale: 0, has: false,
  };

  async function initLandmarker(runningMode) {
    const fileset = await FilesetResolver.forVisionTasks(WASM_ROOT);
    landmarker = await FaceLandmarker.createFromOptions(fileset, {
      baseOptions: { modelAssetPath: MODEL_URL, delegate: 'GPU' },
      runningMode,
      numFaces: 1,
      outputFaceBlendshapes: false,
      outputFacialTransformationMatrixes: false,
    });
  }

  function applyPose(pts) {
    state.landmarks = pts;

    const eyeR = toWorld(pts[LM.eyeOuterR]);
    const eyeL = toWorld(pts[LM.eyeOuterL]);
    const bridge = toWorld(pts[LM.bridge]);
    const chin = toWorld(pts[LM.chin]);
    const brow = toWorld(pts[LM.foreheadTop]);

    // orthonormal head basis straight from the landmarks
    const right = eyeL.clone().sub(eyeR).normalize();
    const upRaw = brow.clone().sub(chin).normalize();
    const forward = right.clone().cross(upRaw).normalize();
    const up = forward.clone().cross(right).normalize();
    const basis = new THREE.Matrix4().makeBasis(right, up, forward);
    const quat = new THREE.Quaternion().setFromRotationMatrix(basis);

    // scale from the measured outer-canthus span
    const span = Math.hypot(eyeL.x - eyeR.x, eyeL.y - eyeR.y);
    const scale = (span / REF_EYE_SPAN) * state.size;

    // sit the bridge of the frame on the nose bridge, pushed slightly forward
    const eyeMid = eyeR.clone().add(eyeL).multiplyScalar(0.5);
    const pos = bridge.clone().lerp(eyeMid, 0.35).add(forward.clone().multiplyScalar(1.1 * scale));

    if (!smooth.has) {
      smooth.pos.copy(pos); smooth.quat.copy(quat); smooth.scale = scale; smooth.has = true;
    } else {
      const a = state.mode === 'photo' ? 1 : 0.4;
      smooth.pos.lerp(pos, a);
      smooth.quat.slerp(quat, a);
      smooth.scale += (scale - smooth.scale) * a;
    }

    anchor.position.copy(smooth.pos);
    anchor.quaternion.copy(smooth.quat);
    anchor.scale.setScalar(smooth.scale);
    fitGroup.position.set(0, state.offsetY, state.offsetZ);
    state.tracking = true;
  }

  let lastTs = -1;
  function loop() {
    requestAnimationFrame(loop);
    // a projection change invalidates the solved pose in still-photo mode
    if (resize() && state.mode === 'photo' && state.landmarks) applyPose(state.landmarks);
    if (state.mode === 'camera' && landmarker && video.readyState >= 2) {
      const ts = performance.now();
      if (video.currentTime !== lastTs) {
        lastTs = video.currentTime;
        const res = landmarker.detectForVideo(video, ts);
        if (res.faceLandmarks && res.faceLandmarks.length) {
          applyPose(res.faceLandmarks[0]);
          if (++probeTick % 8 === 0) estimateLighting(video, res.faceLandmarks[0]);
          hint(null);
        } else {
          state.tracking = false;
          hint('Looking for a face — face the camera in even light');
        }
      }
    }
    anchor.visible = state.tracking;
    renderer.render(scene, camera);
  }

  function hint(msg) {
    const el = $('hint');
    if (!msg) { el.hidden = true; return; }
    el.hidden = false;
    el.textContent = msg;
  }

  function overlay(show, title, text, spin) {
    const ov = $('overlay');
    ov.hidden = !show;
    if (title) $('ov-title').textContent = title;
    if (text) $('ov-text').textContent = text;
    $('spinner').style.display = spin ? 'block' : 'none';
    $('start').style.display = spin ? 'none' : '';
    $('pick-photo').style.display = spin ? 'none' : '';
  }

  /* ---------- camera mode ---------- */
  $('start').addEventListener('click', async () => {
    overlay(true, 'Starting camera…', 'Loading the face tracking model. This happens once.', true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 1280 }, height: { ideal: 960 } },
        audio: false,
      });
      video.srcObject = stream;
      await video.play();
      if (!landmarker) await initLandmarker('VIDEO');
      else await landmarker.setOptions({ runningMode: 'VIDEO' });
      root.classList.remove('is-photo');
      state.mode = 'camera';
      smooth.has = false;
      resize(video.videoWidth, video.videoHeight);
      overlay(false);
    } catch (err) {
      overlay(
        true,
        'Camera unavailable',
        err && err.name === 'NotAllowedError'
          ? 'Camera permission was blocked. Allow it in your browser’s address bar, or use a photo instead.'
          : `Could not start the camera (${err && err.message ? err.message : err}). The page must be served over HTTPS or localhost.`,
        false
      );
    }
  });

  /* ---------- photo mode ---------- */
  $('pick-photo').addEventListener('click', () => $('file').click());
  $('file').addEventListener('change', async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    overlay(true, 'Reading photo…', 'Detecting the face in your image.', true);
    try {
      const url = URL.createObjectURL(file);
      await new Promise((res, rej) => {
        photo.onload = res;
        photo.onerror = rej;
        photo.src = url;
      });
      if (!landmarker) await initLandmarker('IMAGE');
      else await landmarker.setOptions({ runningMode: 'IMAGE' });
      const res = landmarker.detect(photo);
      if (!res.faceLandmarks || !res.faceLandmarks.length) {
        overlay(true, 'No face found', 'Try a well-lit, front-facing photo where the whole head is visible.', false);
        return;
      }
      if (video.srcObject) { video.srcObject.getTracks().forEach((t) => t.stop()); video.srcObject = null; }
      root.classList.add('is-photo');
      stage.classList.remove('is-mirrored'); // an uploaded photo is already correctly oriented
      state.mode = 'photo';
      smooth.has = false;
      resize(photo.naturalWidth, photo.naturalHeight);
      requestAnimationFrame(() => {
        resize();
        applyPose(res.faceLandmarks[0]);
        // no stream to converge over, so run the estimator to settle in one go
        for (let i = 0; i < 60; i++) estimateLighting(photo, res.faceLandmarks[0]);
      });
      overlay(false);
    } catch (err) {
      overlay(true, 'Could not read that image', String(err && err.message ? err.message : err), false);
    }
  });

  /* ---------- face shape ---------- */
  $('analyse').addEventListener('click', () => {
    const box = $('face');
    if (!state.landmarks) {
      box.hidden = false;
      box.innerHTML = 'Start the camera or load a photo first — the analysis needs a detected face.';
      return;
    }
    const { shape } = analyseFace(state.landmarks);
    const picks = SHAPES.filter((s) => s.suits.includes(shape)).slice(0, 5);
    box.hidden = false;
    box.innerHTML = `<strong>${shape[0].toUpperCase() + shape.slice(1)} face.</strong> ${FACE_SHAPES[shape]}
      <ul>${picks.map((p) => `<li><b>${p.name}</b> — ${p.desc.split('.')[0]}.</li>`).join('')}</ul>`;
  });

  /* ---------- snapshots ---------- */
  $('snap').addEventListener('click', () => {
    const src = state.mode === 'photo' ? photo : video;
    const sw = state.mode === 'photo' ? photo.naturalWidth : video.videoWidth;
    const sh = state.mode === 'photo' ? photo.naturalHeight : video.videoHeight;
    if (!sw) return;
    const out = document.createElement('canvas');
    out.width = sw;
    out.height = sh;
    const g = out.getContext('2d');
    const mirrored = stage.classList.contains('is-mirrored');
    if (mirrored) { g.translate(sw, 0); g.scale(-1, 1); }
    g.drawImage(src, 0, 0, sw, sh);
    g.drawImage(canvas, 0, 0, sw, sh);
    const url = out.toDataURL('image/jpeg', 0.9);

    const el = document.createElement('div');
    el.className = 'otr-shot';
    el.innerHTML = `<img src="${url}" alt="${state.shape.name}"><span>${state.shape.name}</span><button title="Remove">×</button>`;
    el.querySelector('button').addEventListener('click', () => el.remove());
    el.querySelector('img').addEventListener('click', () => {
      const a = document.createElement('a');
      a.href = url;
      a.download = `tryon-${state.shape.id}.jpg`;
      a.click();
    });
    $('shots').prepend(el);
    while ($('shots').children.length > 6) $('shots').lastElementChild.remove();
  });

  /* ---------- go ---------- */
  rebuild();
  resize();
  loop();

  return {
    setShape: (id) => {
      const s = SHAPES.find((x) => x.id === id);
      if (s) { state.shape = s; rebuild(); }
    },
    setFinish: (id) => {
      const f = FINISHES.find((x) => x.id === id);
      if (f) { state.finish = f; rebuild(); }
    },
    setLens: (id) => {
      const l = LENSES.find((x) => x.id === id);
      if (l) { state.lens = l; rebuild(); }
    },
    setThickness: (mult) => {
      state.thickness = THREE.MathUtils.clamp(mult, 0.5, 1.6);
      rebuild();
    },
    shapes: SHAPES.map((s) => ({ id: s.id, name: s.name })),
    /* testing hook: solve and apply a pose from a landmark array directly,
       without a camera. Also what photo mode uses internally. */
    debugPose: (pts) => applyPose(pts),
    state,
    three: { scene, camera, renderer, anchor, fitGroup, get frame() { return frameGroup; } },
    destroy: () => {
      if (video.srcObject) video.srcObject.getTracks().forEach((t) => t.stop());
      renderer.dispose();
      root.innerHTML = '';
    },
  };
}

/* Auto-mount for the Shopify snippet: any [data-optic-tryon] on the page. */
function autoMount() {
  document.querySelectorAll('[data-optic-tryon]:not([data-otr-ready])').forEach((el) => {
    el.setAttribute('data-otr-ready', '1');
    mountOpticTryOn(el, {
      shape: el.dataset.shape || undefined,
      finish: el.dataset.finish || undefined,
      lens: el.dataset.lens || undefined,
      theme: el.dataset.theme || 'dark',
    });
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', autoMount);
} else {
  autoMount();
}
document.addEventListener('shopify:section:load', autoMount);